import { ActionTree } from 'vuex';

// Shared types
import { RequestSpace, FromSpacesResponse } from '@coldtec/cos-types';

// Store and store types
import Store from '@/store';
import {
    RootState,
    SpacesGetter,
    SystemsAction,
    SystemsMutation,
    SystemsGetter,
    SystemsFilterGroup,
    SystemSplit,
    SystemsFilters,
    ToolSystem,
    ToolSystemSuggestion,
    RequestAction,
    ToolSpace,
    ToolUnit,
    SpacesAction,
} from '@/store/store.types';

// Api
import Api from '@/api';

/**
 *
 *  Used to reset the space for which the forwarded unit was selected.
 *
 *  (i) This normally happens by the `SystemUnit.vue` watcher
 *  on the `system.selected` state, but the component might
 *  already be destroyed when this point is reached. So we
 *  need to also do it at this point.
 *
 *  @param unit The unit to reset the space for
 *
 */
function resetSpaceIndoorUnit(unit: ToolUnit): void {

    for (const space of Object.values<ToolSpace>(Store.getters[SpacesGetter.ADDED])) {

        if (space.data.tool_space_id !== unit.toolSpaceId) {

            continue;

        }

        space.data.indoor_unit = null;
        space.data.color = null;

    }

    unit.toolSpaceId = null;
    unit.color = null;

}

/**
 *  Holding the actions of the systems.
 */
export const Actions: ActionTree<RootState, RootState> = {

    /**
     *
     *  Used to fetch the available filters.
     *
     *  (i) We decided to bundle the filters in the
     *  tool for now to spare an HTTP request.
     *
     */
    async [SystemsAction.FILTERS_FETCH]({ commit }): Promise<void> {

        const filters: Array<SystemsFilterGroup> = [
            {
                title: 'Wifi',
                column: 'wifi',
                options: [
                    {
                        label: 'Ja',
                        value: 1,
                    },
                    {
                        label: 'Nee',
                        value: 0,
                    },
                ],
            },
        ];

        commit(SystemsMutation.FILTERS_AVAILABLE, filters);

    },

    /**
     *  Used to set the filters.
     */
    async [SystemsAction.FILTERS_SET]({ state, commit, dispatch }, filters: SystemsFilters): Promise<void> {

        commit(SystemsMutation.FILTERS_STATE, filters);

        /**
         *  Ensure the systems are resetted of every space,
         *  because the selected system might not come back
         *  in the filtered results.
         */
        for (const toolSpaceId of Object.keys(state.spaces.added)) {

            dispatch(SpacesAction.RESET_SYSTEM_SELECTION, toolSpaceId);

        }

        /**
         *  Dispatch the action to fetch the suggestions, which on
         *  his turns takes the filters into account.
         */
        dispatch(SystemsAction.FETCH_SUGGESTIONS);

    },

    /**
     *  Used to fetch and store the system suggestions,
     *  based on the configured spaces and filters.
     */
    async [SystemsAction.FETCH_SUGGESTIONS]({ commit, state }): Promise<void> {

        commit(SystemsMutation.LOADING, true);

        /**
         *
         *  Take the set filters into account and construct the
         *  filter params to be sent with the request.
         *
         *  (i) We're always using an Array because a filter might include
         *  multiple options that can be selected at the same time.
         *
         */
        const params: Record<string, Array<string | number>> = {};

        for (const [ column, options ] of Object.entries(state.systems.filters.state)) {

            params[column] = [];

            for (const [ value, active ] of Object.entries(options)) {

                if (!active) {

                    continue;

                }

                params[column].push(value);

            }

        }

        /**
         *  Construct the Array of spaces to request the system suggestions
         *  for, and ensure there are spaces to sent with the request.
         */
        const spaces: Array<RequestSpace> = [];

        for (const space of Object.values(state.spaces.added)) {

            spaces.push(space.data as RequestSpace);

        }

        if (!spaces.length) {

            throw new Error('No spaces to request suggestions for');

        }

        /**
         *  Fetch the suggestions from the API.
         */
        const { data } = await Api.client.post<FromSpacesResponse>('/systems/from-spaces', { spaces }, { params });

        /**
         *  Store the suggestions.
         */
        commit(SystemsMutation.SUGGESTIONS, data);

        return Promise.resolve();

    },

    /**
     *
     *  Used to show the suggestions for a specific space.
     *
     *  (i) Ignored when the provided space is already showing
     *
     *  @param systemSpaceId The systemSpaceId to show the system suggestions for
     *
     */
    [SystemsAction.SHOW_FOR_SPACE]({ commit, state }, systemSpaceId: string): void {

        if (state.systems.suggestions.show === systemSpaceId) {

            return;

        }

        commit(SystemsMutation.LOADING, true);

        commit(SystemsMutation.SUGGESTIONS_SHOW_FOR_SPACE, systemSpaceId);

        /**
         *  It can actually re-render instantly because
         *  we have all data locally, but we show the
         *  loader for a bit to improve UX.
         */
        setTimeout(() => {

            commit(SystemsMutation.LOADING, false);

        }, 250);

    },

    /**
     *
     *  Used to select a system for the currently showing space(s).
     *
     *  (i) There's always showing one space at a time, so when a system gets
     *  selected we know it's for the space that is currently showing.
     *
     *  (i) Every system component is watching the tool system's `selected`
     *  state and acts respectively upon changes. However, in some cases
     *  the tool unit state and spaces should be updated here
     *
     *  @param systemGuid The guid of the system to select for the currently showing space
     *
     */
    [SystemsAction.SELECT]({ state, dispatch }, systemGuid: string): void {

        for (const [ systemSpaceId, suggestion ] of Object.entries<ToolSystemSuggestion>(Store.getters[SystemsGetter.SUGGESTIONS_ALL])) {

            /**
             *  Only check the systems of the space for which
             *  the systems are currently shown.
             */
            if (systemSpaceId !== state.systems.suggestions.show) {

                continue;

            }

            /**
             *  Loop through the systems, update their selected
             *  state accordingly and update the selected system
             *  of the currently showing space(s).
             */
            let newSystemGuid: string | null = systemGuid;

            for (const system of suggestion.systems) {

                /**
                 *
                 *  Toggle the system's selected state if this
                 *  system got selected (again).
                 *
                 *  (i) The `SystemUnit.vue` watches for changes
                 *  on the selected state and updates his indoor
                 *  unit and color automatically
                 *
                 */
                if (system.guid === systemGuid) {

                    system.selected = !system.selected;

                    /**
                     *
                     *  Reset the space's system guid if deselected.
                     *
                     *  (i) Assigned below
                     *
                     */
                    if (!system.selected) {

                        newSystemGuid = null;

                    }

                } else {

                    system.selected = false;

                }

                /**
                 *
                 *  Reset the space's indoor unit when deselected.
                 *
                 *  (!) This normally happens by the `SystemUnit.vue` watcher on the
                 *  `system.selected` state, but the component might already be destroyed
                 *  when this point is reached. So we need to also do it at this point.
                 *
                 */
                if (system.selected) {

                    continue;

                }

                system.indoorUnits.forEach(resetSpaceIndoorUnit);

            }

            /**
             *
             *  Update the selected systems (guid) of the
             *  currently showing space(s).
             *
             *  (i) The systemSpaceId key is composed from the
             *  toolSpaceIds of the spaces for which the system
             *  is selected, separated by '-'.
             *
             *  (i) See the `systems.mutations.ts` file.
             *
             */
            const toolSpaceIds: Array<string> = systemSpaceId.split('-');

            for (const toolSpaceId of toolSpaceIds) {

                state.spaces.added[toolSpaceId].data.system = newSystemGuid;

            }

            /**
             *
             *  Used to check the forwarded type of splits and deselect
             *  them if another type of split got selected for it.
             *
             *  @param split The type of split to check
             *
             */
            const checkSplits = (split: SystemSplit) => {

                for (const suggestion of Object.values<ToolSystemSuggestion>(state.systems.suggestions[split])) {

                    /**
                     *
                     *  Skip checking himself to prevent it from deselecting
                     *  himself in case of a multi split.
                     *
                     *  (i) It's about himself if the suggestion's tool space ids
                     *  are exactly the same as the tool space ids that got updated
                     *
                     */
                    if (suggestion.toolSpaceIds.length === toolSpaceIds.length) {

                        const self: boolean = suggestion.toolSpaceIds.some((toolSpaceId: string) => toolSpaceIds.includes(toolSpaceId));

                        if (self) {

                            continue;

                        }

                    }

                    /**
                     *
                     *  Create the condition to check if the selected states of the
                     *  current suggestions' systems should be reset, because
                     *  another type of split got selected for it.
                     *
                     *  (i) The `toolSpaceIds` contains the tool space ids of spaces for which
                     *  a system just got selected, while the suggestion `toolSpaceIds`
                     *  represent the local ids of the suggestions.
                     *
                     */
                    const deselect = split === SystemSplit.Single
                        ? toolSpaceIds.some((toolSpaceId: string) => suggestion.toolSpaceIds.includes(toolSpaceId))
                        : suggestion.toolSpaceIds.some((toolSpaceId: string) => toolSpaceIds.includes(toolSpaceId));

                    /**
                     *
                     *  Reset the systems selected state and his space's indoor
                     *  units when it should be deselected.
                     *
                     *  (!) This normally happens by the `SystemUnit.vue` watcher
                     *  on the `system.selected` state, but the component might
                     *  already be destroyed when this point is reached. So we
                     *  need to also do it at this point.
                     *
                     */
                    if (!deselect) {

                        continue;

                    }

                    for (const system of suggestion.systems) {

                        system.selected = false;

                        system.indoorUnits.forEach(resetSpaceIndoorUnit);

                    }

                }

            };

            /**
             *
             *  Ensure that only a single or multi split can be selected
             *  for a space, by automatically deselecting the space's
             *  other type of split.
             *
             *  (i) So if a single split has been selected for the space, but a
             *  multi split is selected, this logic ensures the single split
             *  gets deselected. Likewise for the other way around.
             *
             *  (i) The `SystemsSpace.vue` component determines if he is selected
             *  based on the selected states of all the space(s) suggestion
             *  systems. So setting all the selected states to false let's
             *  the space be unselected.
             *
             *  (!) We always check the other type of split, but because a
             *  space can be present in two multi split suggestions we also
             *  check the other multi splits in case of a multi split.
             *
             */
            const splitsToCheck: SystemSplit = toolSpaceIds.length !== 1 ? SystemSplit.Single : SystemSplit.Multi;

            checkSplits(splitsToCheck);

            if (splitsToCheck === SystemSplit.Single) {

                checkSplits(SystemSplit.Multi);

            }

        }

        /**
         *  Dispatch an action to sync the request spaces
         *  to preserve state on navigation.
         */
        dispatch(RequestAction.SYNC_SPACES);

    },

    /**
     *
     *  Used to sync the suggestions (tool systems) with the request data.
     *
     *  (i) Ensuring the state of the suggestions is equal to the state of the request
     *
     */
    [SystemsAction.SYNC_SUGGESTIONS]({ state }): void {

        /**
         *  Check the selected space of every (request) space.
         */
        for (const space of state.request.data.spaces) {

            if (!space.system) {

                continue;

            }

            /**
             *  Get every tool space id for which suggestions are loaded.
             */
            const toolSpaceIds = Object.keys(Store.getters[SystemsGetter.SUGGESTIONS_ALL]);

            /**
             *
             *  Check if the space's local id is present in the
             *  tool space id and check his systems if so.
             *
             *  (i) This ensures that only suggested systems for
             *  this space are checked, and it also checks the systems
             *  in case the space is part of a multi split.
             *
             */
            for (const toolSpaceId of toolSpaceIds) {

                /**
                 *  Only check the systems if his tool space id
                 *  is included in the tool space ids, meaning the
                 *  suggestions were (also) for him.
                 */
                if (!toolSpaceId.includes(space.tool_space_id)) {

                    continue;

                }

                /**
                 *  Get the system suggestions for this specific space.
                 */
                const spaceToolSystems: Array<ToolSystem> = Store.getters[SystemsGetter.SUGGESTIONS_ALL][toolSpaceId].systems;

                /**
                 *  Check every suggestion system if it got selected.
                 */
                for (const system of spaceToolSystems) {

                    /**
                     *  His suggestion is not selected if the system's
                     *  guid is not set as his system.
                     */
                    if (system.guid !== space.system) {

                        continue;

                    }

                    system.selected = true;

                    /**
                     *  Also check the (tool) systems' indoor units.
                     */
                    for (const unit of system.indoorUnits) {

                        /**
                         *  Find the space that was selected for this indoor unit.
                         */
                        for (const space of state.request.data.spaces) {

                            if (space.indoor_unit !== unit.guid) {

                                continue;

                            }

                            /**
                             *
                             *  Ensure the unit wasn't already selected for
                             *  another indoor unit.
                             *
                             *  (!) This might happen if a multi split contains
                             *  an indoor unit multiple times (same guid).
                             *
                             */
                            let alreadySelected = false;

                            for (const indoorUnitSelected of system.indoorUnits) {

                                if (indoorUnitSelected.toolSpaceId !== space.tool_space_id) {

                                    continue;

                                }

                                alreadySelected = true;

                                break;

                            }

                            /**
                             *  Only select the space and his color if not
                             *  already selected for another space.
                             */
                            if (alreadySelected) {

                                continue;

                            }

                            unit.toolSpaceId = space.tool_space_id;

                            if (space.color) {

                                unit.color = space.color;

                            }

                            break;

                        }

                    }

                    /**
                     *  Also check the (tool) systems' accessories.
                     */
                    if (!Array.isArray(space.accessories)) {

                        continue;

                    }

                    for (const accessory of system.accessories) {

                        if (space.accessories.includes(accessory.guid)) {

                            accessory.selected = true;

                        }

                    }

                    break;

                }

            }

        }

    },

};
